import { useEffect, useMemo, useState } from 'react'
import useConfig from './useConfig'
import { useTranslation } from 'react-i18next'
import useBalances from './useBalances'
import { CKBTC_MINTER_CANISTER_ID } from '../constants/canisterIds'
import { decimalsToE8s } from '../utils/validationHelper'
import { CkBTCMinterCanister } from '@dfinity/ckbtc'
import {
  approveErc20,
  depositErc20,
  getIdentity,
  mintCKEth,
  sendBTC,
} from '../handlers/walletActions/wallet'
import useWallet from './useWallet'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { GAEventName } from '../interfaces/googleAnalytics'
import { BTC_ID, CKBTC_ICP_ID, CKETH_ICP_ID, ETH_ID } from '../constants/tokenIds'
import {
  ERC_20_DEPOSIT_GAS,
  useBtcBaseFee,
  useErc20ApproveGas,
  useEthDepositGas,
  useETHGasFee,
} from './useGas'
import { getEthProvider } from '../utils/getEthProvider'
import { BTCNFT, Chain, PlugToken } from '../interfaces/common'
import { logger } from '../logger'
import BitcoinClient from '../clients/BitcoinClient'
import { Principal } from '@dfinity/principal'
import { UTXO } from '../interfaces/walletActions'
import getAgent from '../utils/getAgent'
import LedgerScannerClient from '../clients/LedgerScannerClient'

interface Props {
  tokenId?: string
}

const useMintCk = ({ tokenId }: Props) => {
  const { tokens, ckerc20Tokens, tokenPricesWithChange } = useConfig()
  const [mintReviewVisible, setMintReviewVisible] = useState(false)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [mintSuccess, setMintSuccess] = useState(false)
  const [mintMode, setMintMode] = useState<'mint' | 'unmint'>('mint')
  const [amount, setAmount] = useState<string>('')
  const [balance, setBalance] = useState<number>(0)
  const [utxos, setUtxos] = useState<UTXO[]>([])
  const [loading, setLoading] = useState(false)
  const [btcAddress, setBtcAddress] = useState<string>('')
  const [token, setToken] = useState<PlugToken | undefined>(
    tokens.find((t) => t.id === (tokenId || ETH_ID)) || undefined
  )
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const { findBalance, refreshBalance, walletNfts } = useBalances()
  const { wallet } = useWallet()
  const { fireEvent } = useGoogleAnalytics()
  const { t } = useTranslation()
  const btcBaseFee = useBtcBaseFee(true)
  const ethDepositGas = useEthDepositGas(Chain.ETH)
  const erc20ApproveGas = useErc20ApproveGas(
    token?.standard === 'ERC20' ? token.address : undefined,
    Math.ceil(Number(amount) * 10 ** (token?.decimals || 0)),
    Chain.ETH,
    undefined,
    true
  )
  const {
    maxFeePerGas,
    maxPriorityFeePerGas,
    failed: gasFailed,
  } = useETHGasFee(true, Chain.ETH, undefined, undefined, undefined, true)
  const ethBalance = findBalance({ id: ETH_ID })

  const provider = useMemo(() => {
    return getEthProvider(Chain.ETH)
  }, [])

  const ordinals = walletNfts.flatMap((collection) =>
    collection.nfts.filter((nft) => nft.standard === 'ordinal')
  ) as BTCNFT[]

  useEffect(() => {
    const balance = findBalance({ id: token?.id })
    if (balance?.balance) {
      setBalance(balance.balance)
    }
  }, [token])

  useEffect(() => {
    const getBtcAddress = async () => {
      const identity = await getIdentity(wallet.principalId)
      const agent = getAgent({ identity })
      const minter = CkBTCMinterCanister.create({
        agent,
        canisterId: Principal.from(CKBTC_MINTER_CANISTER_ID),
      })

      minter
        .getBtcAddress({ owner: Principal.from(wallet.principalId) })
        .then(setBtcAddress)
        .catch((e) => logger.error('Failed to get BTC address', { originalError: e }))
    }

    getBtcAddress()
  }, [wallet.principalId])

  useEffect(() => {
    if (!wallet.btcAddress) return

    BitcoinClient.getUTXOs(wallet.btcAddress)
      .then((utxos) => {
        const filteredUtxos = utxos.filter(
          (utxo) =>
            !ordinals.some(
              (ordinal) => utxo.mintTxid === ordinal.txId && utxo.mintIndex === ordinal.mintIndex
            )
        )
        setUtxos(filteredUtxos)
      })
      .catch(logger.debug)
  }, [wallet.btcAddress, walletNfts])

  const updateCkBTCBalance = async () => {
    const identity = await getIdentity(wallet.principalId)
    const agent = getAgent({ identity })
    const minter = CkBTCMinterCanister.create({
      agent,
      canisterId: Principal.from(CKBTC_MINTER_CANISTER_ID),
    })

    setBalanceLoading(true)

    minter
      .updateBalance({ owner: Principal.from(wallet.principalId) })
      .catch((e) => logger.error('Failed to update ckBTC balance', { originalError: e }))
      .finally(() => setBalanceLoading(false))
  }

  const ckToken = useMemo(() => {
    if (!token) return

    if (token.standard === 'ETH') return tokens.find((token) => token.id === CKETH_ICP_ID)
    if (token.id === BTC_ID) return tokens.find((token) => token.id === CKBTC_ICP_ID)

    const ckErcToken = ckerc20Tokens?.find(
      (ercToken) => ercToken.erc20_contract_address.toLowerCase() === token.address?.toLowerCase()
    )

    return tokens.find((token) => token.address === ckErcToken?.ledger_canister_id?.toString())
  }, [ckerc20Tokens, token, tokens])

  const totalFee = useMemo(() => {
    if (!token) return

    if (token.chain === Chain.ETH && !maxFeePerGas) {
      return
    }

    let fee = 0

    if (token.id === BTC_ID) {
      if (!btcBaseFee || !amount || !wallet.btcAddress) return

      fee =
        (BitcoinClient.estimateFee(
          utxos,
          ordinals as BTCNFT[],
          wallet.btcAddress,
          btcAddress,
          parseInt(decimalsToE8s(amount, 8)),
          btcBaseFee
        ) || 0) *
        10 ** -8
    } else if (token.id === ETH_ID && ethDepositGas) {
      fee = maxFeePerGas! * Number(ethDepositGas) * 10 ** -18
    } else if (erc20ApproveGas) {
      fee = (maxFeePerGas! * erc20ApproveGas + ERC_20_DEPOSIT_GAS) * 10 ** -18
    } else {
      return
    }

    const price = tokenPricesWithChange[token.id === BTC_ID ? BTC_ID : ETH_ID]?.price

    return {
      fee,
      feeMoney: price ? price * fee : undefined,
    }
  }, [
    ethDepositGas,
    maxFeePerGas,
    erc20ApproveGas,
    token,
    tokenPricesWithChange,
    amount,
    btcAddress,
  ])

  const maxAmount = token?.standard === 'ERC20' ? balance : balance - (totalFee?.fee || 0)

  const error = useMemo(() => {
    const parsedAmount = Number(amount)

    if (!parsedAmount) return t('mintCk.errors.noAmount')
    if (!totalFee) return t('mintCk.errors.fetchingGas')

    if (token?.id === BTC_ID) {
      if (parsedAmount + totalFee.fee > balance || totalFee.fee == 0) {
        return t('mintCk.errors.notEnough', { token: 'BTC' })
      }
    } else if (token?.id === ETH_ID) {
      if (parsedAmount + totalFee.fee > (ethBalance?.balance || 0)) {
        return t('mintCk.errors.notEnough', { token: 'ETH' })
      }
    } else {
      if (parsedAmount > balance) {
        return t('mintCk.errors.notEnough', { token: token?.symbol })
      }

      if (totalFee.fee > (ethBalance?.balance || 0)) {
        return t('mintCk.errors.notEnoughForGas', { token: 'ETH' })
      }
    }

    if (gasFailed) return t('common.errors.gasError')
  }, [amount, balance, token, totalFee, gasFailed])

  const mintCKErc20 = async () => {
    if (!token) throw t('mintCk.errors.tokenNotFound')
    if (!erc20ApproveGas || !maxFeePerGas || !maxPriorityFeePerGas) {
      throw t('mintCk.errors.noGasFee')
    }

    if (!wallet.ethAddress || !amount) {
      throw t('mintCk.errors.noData')
    }

    const nonce = await provider.getTransactionCount({
      address: wallet.ethAddress as `0x${string}`,
    })

    await approveErc20({
      amount: decimalsToE8s(amount, token.decimals),
      gasLimit: erc20ApproveGas.toString(),
      maxFeePerGas: maxFeePerGas.toString(),
      maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
      nonce: nonce.toString(),
      contractAddress: token.address!,
      chain: Chain.ETH,
    })

    const newNonce = await provider.getTransactionCount({
      address: wallet.ethAddress as `0x${string}`,
    })

    await depositErc20({
      amount: decimalsToE8s(amount, token.decimals),
      gasLimit: ERC_20_DEPOSIT_GAS.toString(),
      maxFeePerGas: maxFeePerGas.toString(),
      maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
      nonce: newNonce.toString(),
      contractAddress: token.address!,
      chain: Chain.ETH,
    })
  }

  const mintCKETH = async () => {
    if (!ethDepositGas || !maxFeePerGas || !maxPriorityFeePerGas) {
      throw t('mintCk.errors.noGasFee')
    }

    if (!wallet.ethAddress || !amount) {
      throw t('mintCk.errors.noData')
    }

    const nonce = await provider.getTransactionCount({
      address: wallet.ethAddress as `0x${string}`,
    })

    await mintCKEth({
      amount: decimalsToE8s(amount, 18),
      gasLimit: ethDepositGas.toString(),
      maxFeePerGas: maxFeePerGas.toString(),
      maxPriorityFeePerGas: maxPriorityFeePerGas.toString(),
      nonce,
      chain: Chain.ETH,
    })

    refreshBalance(token!)
  }

  const mintCKBTC = async () => {
    if (!totalFee?.fee || !btcAddress || !amount) {
      throw t('mintCk.errors.noData')
    }

    const sendResponse = await sendBTC({
      to: btcAddress,
      amount: decimalsToE8s(amount, 8),
      utxos,
      fee: Math.round(totalFee.fee * 10 ** 8).toString(),
    })

    return sendResponse
  }

  const performMint = async () => {
    setLoading(true)

    let sendAction

    switch (token?.id) {
      case BTC_ID:
        sendAction = mintCKBTC
        break
      case ETH_ID:
        sendAction = mintCKETH
        break
      default:
        sendAction = mintCKErc20
        break
    }

    try {
      await sendAction()
      setMintSuccess(true)

      fireEvent(GAEventName.MINT, {
        token: token!.symbol,
        amount: Number(amount),
      })

      LedgerScannerClient.refreshTransactions(wallet)
    } catch (e) {
      logger.error('Failed to mint CK', { originalError: e })
      setPrompt({
        title: t(`mintCk.${mintMode}Failed`),
        description: t(`mintCk.mintFailedDescription`),
      })
      setMintReviewVisible(false)
    } finally {
      setLoading(false)
    }
  }

  return {
    error,
    performMint,
    token,
    setToken,
    amount,
    setAmount,
    fee: totalFee,
    loading,
    ckToken,
    mintSuccess,
    mintReviewVisible,
    setMintReviewVisible,
    ckerc20Tokens,
    tokens,
    mintMode,
    setMintMode,
    prompt,
    setPrompt,
    ordinals,
    maxAmount,
    updateCkBTCBalance,
    balanceLoading,
  }
}

export default useMintCk
